import type { RadarBubbleLesson } from '@/services/api/teacher'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

interface LessonListProps {
  lessons: RadarBubbleLesson[]
  compact?: boolean
}

const formatDateShort = (dateStr: string | null): string | null => {
  if (!dateStr) return null
  const date = new Date(dateStr)
  return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })
}

const formatDateWithWeekday = (dateStr: string | null): string | null => {
  if (!dateStr) return null
  const date = new Date(dateStr)
  return date.toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit' })
}

// Status -> Textfarbe fuer Dark-Glass-Tooltip (pool-fix-v2).
// Konsistent mit den Radar-Bubble-Farben.
const STATUS_TEXT_COLOR: Record<string, string> = {
  confirmed: '#86efac',       // green-300 — nur Annahme
  could_cover: '#9ca3af',     // gray-400 — Verfügbarkeit, nicht angenommen
  filled: '#86efac',
  accepted: '#86efac',
  assigned: '#86efac',
  booked: '#86efac',
  interested: '#93c5fd',      // blue-300
  waiting: '#93c5fd',
  declined: '#fca5a5',        // red-300
  not_available: '#fca5a5',
  expired: '#9ca3af',
  pending: '#fdba74',         // orange-300
  pending_active: '#fdba74',
  pending_timeout: '#fdba74',
  partially_filled: '#fdba74',
  assigned_elsewhere: '#9ca3af', // gray-400
  cancelled: '#9ca3af',
}

function getStatusColor(status: string | null | undefined): string {
  if (status && STATUS_TEXT_COLOR[status]) return STATUS_TEXT_COLOR[status]
  return '#fdba74' // default orange
}

/**
 * Structured lesson list for radar tooltips (dark glass variant).
 * Renders a compact table-like display of lessons with date, time slot, subject,
 * class, and room. Used by GlassTooltipContent (FloatingTooltip, RequestListTooltip,
 * HoveredBubbleInfo) — the single shared hover layout.
 *
 * Phase anzeige-v3: cross-day date display.
 */
export function LessonList({ lessons, compact = false }: LessonListProps) {
  if (!lessons || lessons.length === 0) {
    return null
  }

  // Single lesson: compact display
  if (lessons.length === 1) {
    const lesson = lessons[0]!
    return (
      <div className="text-xs text-gray-400">
        {lesson.subject_name || '—'}
        {lesson.klass_name && ` | ${lesson.klass_name}`}
        {lesson.room_name && ` | ${lesson.room_name}`}
      </div>
    )
  }

  // Multiple lessons: structured list
  const hasTimeSlots = lessons.some(l => l.time_slot_name)
  const hasRooms = lessons.some(l => l.room_name)
  const uniqueDates = new Set(lessons.map(l => l.date).filter(Boolean))
  const hasMultipleDates = uniqueDates.size > 1

  return (
    <div
      className="text-xs space-y-1"
      style={{
        maxHeight: compact ? '120px' : '180px',
        overflowY: lessons.length > 5 ? 'auto' : 'visible',
      }}
    >
      {sortLessonsByDateTime(lessons).map((lesson, idx) => {
        const statusColor = getStatusColor(lesson.status)
        return (
        <div
          key={idx}
          className="flex items-baseline gap-2"
          style={{ fontFamily: 'ui-monospace, monospace', fontSize: '11px', borderLeft: `2px solid ${statusColor}`, paddingLeft: '4px' }}
        >
          {hasMultipleDates && (
            <span className="text-gray-400 min-w-[55px] shrink-0">
              {(compact ? formatDateShort(lesson.date) : formatDateWithWeekday(lesson.date)) || '—'}
            </span>
          )}
          {hasTimeSlots && (
            <span className="text-gray-500 min-w-[80px] shrink-0">
              {lesson.time_slot_name || '—'}
            </span>
          )}
          <span className="font-medium min-w-[60px] shrink-0" style={{ color: statusColor }}>
            {lesson.subject_name || '—'}
          </span>
          <span className="text-gray-400 min-w-[40px] shrink-0">
            {lesson.klass_name || '—'}
          </span>
          {hasRooms && (
            <span className="text-gray-500 shrink-0">
              {lesson.room_name || '—'}
            </span>
          )}
        </div>
        )
      })}
    </div>
  )
}
